import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_UI,
  FONT_HEADING,
  SKIN_THEMES,
} from "../layouts/constants";

const EMOJIS = [
  "🚨", "🚔", "🛑", "⚠️", "🔥", "🧯", "🔒", "🔓", "🗝️", "📞",
  "📱", "💡", "🔌", "👮", "🕵️", "🚒", "🚓", "🚑", "🏁", "🎯",
  "🚪", "🔦", "📷", "🎥", "📦", "📄", "🧾", "✍️", "✅", "❌",
  "🟢", "🟡", "🔵", "🟣",
];

const initialTypeForm = {
  label: "",
  type_key: "",
  icon_kind: "emoji",
  icon_value: "⚠️",
  color: "#2980b9",
  is_active: true,
  sort_order: 50,
};

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

export default function AddIncidents() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [typeForm, setTypeForm] = useState(initialTypeForm);
  const [keyTouched, setKeyTouched] = useState(false);

  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  const fetchPageData = async (local, token) => {
    try {
      const [dashboardRes, usersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${API_BASE_URL}/incidents/guards`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!usersRes.ok) throw new Error("Failed to fetch guards");

      const dashboardResult = await dashboardRes.json();
      const usersResult = await usersRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      const allUsers = usersResult?.data || [];
      setUsers(Array.isArray(allUsers) ? allUsers : []);
    } catch (error) {
      console.error("Add incidents page error:", error);
      toast.error(error.message || "Failed to load page data.");
    } finally {
      setLoading(false);
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const filteredUsers = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return users;

    return users.filter((user) => {
      const memberId = String(user.member_id || "").toLowerCase();
      const name = String(user.name || "").toLowerCase();
      const username = String(user.username || "").toLowerCase();

      return memberId.includes(keyword) || name.includes(keyword) || username.includes(keyword);
    });
  }, [users, search]);

  const handleSubmitType = async (e) => {
    e.preventDefault();

    if (!typeForm.label.trim() || !typeForm.type_key.trim() || !typeForm.icon_value.trim()) {
      toast.error("Please fill Label, Type Key and Icon.");
      return;
    }

    if (!/^#[0-9a-fA-F]{6}$/.test(typeForm.color)) {
      toast.error("Please select a valid hex color.");
      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const payload = {
        label: typeForm.label.trim(),
        type_key: slugify(typeForm.type_key.trim()),
        icon_kind: typeForm.icon_kind,
        icon_value: typeForm.icon_value.trim(),
        color: typeForm.color,
        is_active: typeForm.is_active ? 1 : 0,
        sort_order: Number(typeForm.sort_order || 100),
      };

      const res = await fetch(`${API_BASE_URL}/incidents/types`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to create incident type");
      }

      toast.success(`Incident type "${payload.label}" created successfully!`);
      setTypeForm(initialTypeForm);
      setKeyTouched(false);
      setShowModal(false);
    } catch (error) {
      console.error("Create incident type error:", error);
      toast.error(error.message || "Failed to create incident type.");
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = (memberId) => {
    navigate(`/incident_per?mode=edit&member_id=${encodeURIComponent(memberId)}`);
  };

  const handleSentinelGuardList = () => {
    navigate("/sentinel_incident");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading incident types page...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="incidents"
      activeMenu="incidents"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />
      
      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Incident Types
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Manage all incident types for guards
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                User Incident Type Management
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Add new incident types and assign them to guards
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={handleSentinelGuardList}
                className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
                style={{
                  background: "var(--surface)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--surface-border)",
                }}
              >
                For Sentinel
              </button>
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                + Add New Type
              </button>
            </div>
          </div>
        </div>

        {/* Users Table Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="mb-3.5">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
              Guard Incident Assignment
            </h3>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Search guards and manage their incident type mapping
            </p>
          </div>

          <div className="mb-4">
            <input
              type="text"
              placeholder="Search by member ID, name, or username..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
            />
          </div>

          <div className="overflow-x-auto rounded-xl border border-[var(--surface-border)]">
            <table className="w-full min-w-[850px] border-collapse text-[13px] font-['Inter',system-ui,sans-serif]">
              <thead>
                <tr style={{ background: "var(--btn-bg)" }}>
                  <th className="text-left px-4 py-3.5 text-[12px] font-extrabold uppercase tracking-wider text-white">
                    Member ID
                  </th>
                  <th className="text-left px-4 py-3.5 text-[12px] font-extrabold uppercase tracking-wider text-white">
                    User
                  </th>
                  <th className="text-left px-4 py-3.5 text-[12px] font-extrabold uppercase tracking-wider text-white">
                    Username
                  </th>
                  <th className="text-left px-4 py-3.5 text-[12px] font-extrabold uppercase tracking-wider text-white">
                    Role
                  </th>
                  <th className="text-left px-4 py-3.5 text-[12px] font-extrabold uppercase tracking-wider text-white">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-6 text-center text-[var(--text-muted)]">
                      No users found in the database.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user, index) => (
                    <tr 
                      key={`${user.member_id}-${index}`} 
                      className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors"
                    >
                      <td className="px-4 py-3.5 text-[13px] align-middle">
                        <span className="text-[var(--accent)] font-extrabold">
                          {user.member_id}
                        </span>
                      </td>

                      <td className="px-4 py-3.5 text-[13px] align-middle">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-[var(--btn-bg)] text-[var(--btn-text)] flex items-center justify-center font-extrabold shadow-[0_3px_12px_var(--accent-glow)]">
                            {String(user.name || "?").charAt(0).toUpperCase()}
                          </div>
                          <div className="font-extrabold text-[var(--text-primary)]">
                            {user.name}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3.5 text-[13px] text-[var(--text-primary)] align-middle">
                        {user.username}
                      </td>

                      <td className="px-4 py-3.5 text-[13px] align-middle">
                        <span className="inline-flex px-3 py-1.5 rounded-full text-[11.5px] font-extrabold bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.28)] text-[#22c55e]">
                          {user.role || "Guard"}
                        </span>
                      </td>

                      <td className="px-4 py-3.5 text-[13px] align-middle">
                        <button
                          type="button"
                          onClick={() => handleEditUser(user.member_id)}
                          className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                          style={{
                            background: "var(--btn-bg)",
                            color: "var(--btn-text)",
                            boxShadow: "0 4px 18px var(--accent-glow)",
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal */}
        {showModal && (
          <IncidentTypeModal
            form={typeForm}
            setForm={setTypeForm}
            setKeyTouched={setKeyTouched}
            onClose={() => setShowModal(false)}
            onSubmit={handleSubmitType}
            saving={saving}
          />
        )}
      </div>
    </Layout>
  );
}

function slugify(value = "") {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function IncidentTypeModal({
  form,
  setForm,
  setKeyTouched,
  onClose,
  onSubmit,
  saving,
}) {
  const previewLabel = form.label || "New Type";
  const previewIcon = form.icon_value || "⚠️";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;

    setForm((prev) => {
      const updated = { ...prev, [name]: nextValue };

      if (name === "label" && !prev.type_key) {
        updated.type_key = slugify(value);
      }

      return updated;
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4 z-[2500]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[760px] bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--divider)]">
          <h3 className="m-0 text-[22px] font-extrabold text-[var(--text-primary)]">
            Add Incident Type
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="border-0 bg-transparent text-3xl leading-none cursor-pointer text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          >
            ×
          </button>
        </div>

        <form onSubmit={onSubmit}>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-5">
              <div
                className="h-11 w-11 rounded-xl flex items-center justify-center text-xl"
                style={{ background: form.color }}
              >
                {previewIcon}
              </div>
              <div className="text-base font-extrabold text-[var(--text-primary)]">
                {previewLabel}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Label <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <input
                  name="label"
                  value={form.label}
                  onChange={handleChange}
                  placeholder="Theft"
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Type Key <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <input
                  name="type_key"
                  value={form.type_key}
                  onChange={(e) => {
                    setKeyTouched(true);
                    handleChange(e);
                  }}
                  placeholder="theft"
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Icon Value <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <input
                  name="icon_value"
                  value={form.icon_value}
                  onChange={handleChange}
                  placeholder="Pick or paste emoji"
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Color
                </label>
                <input
                  type="color"
                  name="color"
                  value={form.color}
                  onChange={handleChange}
                  className="w-full h-[46px] p-1.5 rounded-xl border border-[var(--surface-border)] outline-none bg-[var(--surface)] cursor-pointer"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Sort Order
                </label>
                <input
                  type="number"
                  name="sort_order"
                  value={form.sort_order}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="inline-flex items-center gap-2 text-[13px] font-extrabold text-[var(--text-primary)] cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleChange}
                  className="w-4 h-4 accent-[var(--accent)] cursor-pointer"
                />
                Enabled
              </label>
            </div>

            <div className="mt-5">
              <div className="text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-2">
                Emoji Picker
              </div>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(42px,1fr))] gap-2 p-2.5 border border-[var(--surface-border)] rounded-xl bg-[var(--surface)] max-h-[180px] overflow-y-auto">
                {EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        icon_kind: "emoji",
                        icon_value: emoji,
                      }))
                    }
                    className={`rounded-xl px-1.5 py-2 text-xl border cursor-pointer transition-colors ${
                      form.icon_value === emoji
                        ? "border-[var(--accent)] bg-[var(--accent-faint)]"
                        : "border-transparent bg-[var(--card-bg)] hover:bg-[var(--accent-faint)]"
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--divider)] bg-[var(--surface)]">
            <button
              type="button"
              onClick={onClose}
              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="border-0 rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              {saving ? "Saving..." : "Save Type"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}